import React, { useState, useEffect, useRef } from 'react';
import { heroSlides } from '../mock';
import './HeroCarousel.css';

const SWIPE_THRESHOLD = 60; // px

const HeroCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [trackWidth, setTrackWidth] = useState(0);
  const timerRef = useRef(null);
  const dragRef = useRef({ active: false, startX: 0, lastX: 0 });
  const trackRef = useRef(null);

  const resetTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 15000);
  };

  useEffect(() => {
    resetTimer();
    return () => clearInterval(timerRef.current);
  }, []);

  useEffect(() => {
    const update = () => {
      if (trackRef.current) setTrackWidth(trackRef.current.offsetWidth);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const goTo = (index) => {
    setCurrentSlide((index + heroSlides.length) % heroSlides.length);
    resetTimer();
  };

  const handlePointerDown = (e) => {
    if (e.target.closest('button') || e.target.closest('.slick-dots')) return;
    dragRef.current = { active: true, startX: e.clientX, lastX: e.clientX };
    setIsDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (!dragRef.current.active) return;
    dragRef.current.lastX = e.clientX;
    setDragOffset(e.clientX - dragRef.current.startX);
  };

  const endDrag = (e) => {
    if (!dragRef.current.active) return;
    const delta = dragRef.current.lastX - dragRef.current.startX;
    dragRef.current.active = false;
    setIsDragging(false);
    setDragOffset(0);
    if (delta <= -SWIPE_THRESHOLD) goTo(currentSlide + 1);
    else if (delta >= SWIPE_THRESHOLD) goTo(currentSlide - 1);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.debug('Pointer already released:', err);
    }
  };

  const translateX = trackWidth
    ? -(currentSlide * trackWidth) + dragOffset
    : 0;

  return (
    <section className="section-banner">
      <div
        className="unifi-slider-custom"
        ref={trackRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        style={{
          cursor: isDragging ? 'grabbing' : 'grab',
          touchAction: 'pan-y',
          position: 'relative',
        }}
      >
        <div style={{ overflow: 'hidden' }}>
        <div
          className="slides-track"
          style={{
            display: 'flex',
            width: `${heroSlides.length * 100}%`,
            transform: `translate3d(${translateX}px, 0, 0)`,
            transition: isDragging
              ? 'none'
              : 'transform 700ms cubic-bezier(0.22, 1, 0.36, 1)',
            willChange: 'transform',
          }}
        >
          {heroSlides.map((slide) => (
            <div
              key={slide.id}
              className="hero-slide"
              style={{ width: `${100 / heroSlides.length}%`, flexShrink: 0 }}
            >
              <div className="slide-container">
                <div className="slide-text">
                  <span className="slide-subhead">{slide.subtitle}</span>
                  <h1>
                    {slide.title.split('\n').map((line, i, arr) => (
                      <React.Fragment key={`${slide.id}-line-${i}`}>
                        {line}
                        {i < arr.length - 1 && <br />}
                      </React.Fragment>
                    ))}
                  </h1>
                </div>
                <div className="slide-image-wrapper">
                  <img src={slide.image} alt={slide.subtitle} draggable={false} />
                </div>
              </div>
            </div>
          ))}
        </div>
        </div>

        <ul className="slick-dots">
          {heroSlides.map((slide, index) => (
            <li key={slide.id} className={index === currentSlide ? 'slick-active' : ''}>
              <button
                type="button"
                onClick={() => goTo(index)}
                aria-label={`Slide ${index + 1}`}
              />
            </li>
          ))}
        </ul>
      </div>

      <div className="banner-element">
        <img src="/images/hero/bg-element.png" alt="" draggable={false} />
      </div>
      <div className="curve-shape"></div>
    </section>
  );
};

export default HeroCarousel;
